import { useMemo, useState } from 'react';
import { navigate, useRoute } from '@/ui/router';
import { EmptyState } from '@/ui/components/bits';
import { searchVocabulary, VOCABULARY, type VocabTerm } from '@/domain/vocabulary';

/**
 * Every term in the textbook's glossary, by chapter, with a definition.
 *
 * The definitions are written for this app, not copied from the book — the
 * glossary's prose is the publisher's. Tapping a term still searches the bank,
 * where the worked examples and his own notes are, so the definition here is
 * the short answer and the bank is the long one.
 *
 * Definitions can be hidden, which turns the page into a bare word list: good
 * for printing, and good for covering the answer and testing yourself.
 */
/** "1-6", or "3", as the URL writes a chapter range. */
function parseChapters(raw: string | null): Set<number> | null {
  if (!raw) return null;
  const range = /^(\d{1,2})-(\d{1,2})$/.exec(raw);
  if (range) {
    const from = Number(range[1]);
    const to = Number(range[2]);
    if (from > to) return null;
    return new Set(Array.from({ length: to - from + 1 }, (_, i) => from + i));
  }
  const single = /^\d{1,2}$/.test(raw) ? Number(raw) : null;
  return single === null ? null : new Set([single]);
}

export function VocabularyScreen() {
  const route = useRoute();
  const [query, setQuery] = useState('');
  const [defined, setDefined] = useState(true);

  // The mid-term covers chapters 1–6, so it links here asking for only those.
  const chapters = parseChapters(route.params.get('ch'));

  /** How many terms are in scope at all, before the search box narrows it. */
  const total = chapters ? VOCABULARY.filter((v) => chapters.has(v.chapter)).length : VOCABULARY.length;

  const matches = useMemo(() => {
    const found = searchVocabulary(query);
    return chapters ? found.filter((v) => chapters.has(v.chapter)) : found;
    // `chapters` is rebuilt each render from the URL; its contents are what
    // matter, and the URL string is the stable thing to depend on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, route.params.get('ch')]);

  const byChapter = useMemo(() => {
    const map = new Map<number, VocabTerm[]>();
    for (const entry of matches) {
      const list = map.get(entry.chapter);
      if (list) list.push(entry);
      else map.set(entry.chapter, [entry]);
    }
    // Searching puts term matches ahead of definition matches, so each
    // chapter's list has to be put back into alphabetical order here.
    for (const list of map.values()) list.sort((a, b) => a.term.localeCompare(b.term, 'en'));
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [matches]);

  return (
    <div className="stack">
      <div className="spread">
        <h1>Vocabulary</h1>
        <div className="row print-hide">
          <button className="btn btn--sm" onClick={() => setDefined((d) => !d)}>
            {defined ? 'Hide definitions' : 'Show definitions'}
          </button>
          <button className="btn btn--sm" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </div>

      <p className="faint" style={{ margin: 0 }}>
        {chapters ? (
          <>
            The {total} terms from chapters {[...chapters][0]}–{[...chapters].at(-1)}, defined in
            plain words.{' '}
            <button className="btn btn--sm btn--ghost" onClick={() => navigate('/vocab')}>
              Show all {VOCABULARY.length}
            </button>
          </>
        ) : (
          <>
            All {VOCABULARY.length} terms from the textbook glossary, by chapter, defined in plain
            words. Tap a term to search your own cards and questions for it.
          </>
        )}
      </p>

      <input
        className="input print-hide"
        type="search"
        value={query}
        spellCheck={false}
        aria-label="Find a term"
        placeholder="Find a term, or describe it"
        onChange={(e) => setQuery(e.target.value)}
      />

      {query.trim() ? (
        <p className="faint" style={{ margin: 0 }}>
          {matches.length} of {total} terms match.
        </p>
      ) : null}

      {matches.length === 0 ? (
        <EmptyState icon="⌕" title="No term matches that">
          The search looks at the definitions as well as the words, so try describing it — or try a
          shorter piece of the word.
        </EmptyState>
      ) : null}

      {byChapter.map(([chapter, terms]) => (
        <section key={chapter} className="stack vocab__chapter">
          <h2 className="section-title">
            Chapter {chapter}
            <span className="faint"> · {terms.length}</span>
          </h2>
          <ul className={defined ? 'vocab__list vocab__list--defined' : 'vocab__list'}>
            {terms.map((entry) => (
              <li key={`${entry.chapter}-${entry.term}`}>
                <button
                  className="vocab__term"
                  onClick={() => navigate('/search', { q: entry.term })}
                  title={`Search your bank for ${entry.term}`}
                >
                  <span>{entry.term}</span>
                  <span className="mono faint">{entry.section}</span>
                </button>
                {defined ? <p className="vocab__def">{entry.definition}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
